/**
 * Module 00 — Global Shell · Shell container (FE-00-02 … FE-00-10)
 *
 * Composes the sidebar + page content + command palette, and owns the global
 * ⌘K / Esc handling and theme reflection onto the DOM. There is no top bar —
 * all global chrome (scope switchers, theme/mode/help, the user menu) lives in
 * the sidebar, so page content runs full-height. When mode is `simple` the whole
 * UI is replaced by the Simple Mode surface (SM-01); toggling back restores the
 * advanced shell where the user left off (SM-02).
 *
 * State comes from the UI store (chrome) and the scope store (hydrated from the
 * session bootstrap); both are RBAC-filtered before render so forbidden nav
 * never flashes.
 */

'use client';

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGenerateApiKey } from '@/lib/hooks/useGenerateApiKey';
import { Sidebar } from './Sidebar';
import { ShellCommandPalette } from './ShellCommandPalette';
import { SimpleMode } from './simple/SimpleMode';
import { useUiStore } from '@/lib/stores/ui-store';
import { useScopeStore, useSimpleOnly, setStoredOrgId } from '@/lib/stores/scope-store';
import { sessionService, type OrgRef } from '@/lib/api/services/session';
import { toast } from 'sonner';
import { filterNav, canViewBilling } from '@/lib/shell/nav';
import { isOrgAdminRole } from '@/lib/governance/org-admin';
import { rbac } from '@/components/settings/_shared';
import { persistTheme, type Mode } from '@/lib/shell/persistence';
import { useShellNav } from '@/lib/shell/useShellNav';
import { useConnectionInfo } from '@/lib/hooks/useConnectionInfo';
import { workspacesFromProjects } from './simple/constants';
import { projectsService } from '@/lib/api/services/projects';

export function Shell({ children }: { children: React.ReactNode }) {
  const { route, go } = useShellNav();

  const collapsed = useUiStore((s) => s.collapsed);
  const setCollapsed = useUiStore((s) => s.setCollapsed);
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const mode = useUiStore((s) => s.mode);
  const setMode = useUiStore((s) => s.setMode);
  const paletteOpen = useUiStore((s) => s.paletteOpen);
  const setPaletteOpen = useUiStore((s) => s.setPaletteOpen);
  const togglePalette = useUiStore((s) => s.togglePalette);

  const user = useScopeStore((s) => s.user);
  const org = useScopeStore((s) => s.org);
  const credits = useScopeStore((s) => s.credits);
  const department = useScopeStore((s) => s.department);
  const project = useScopeStore((s) => s.project);
  const availableDepartments = useScopeStore((s) => s.availableDepartments);
  const availableProjects = useScopeStore((s) => s.availableProjects);
  const availableOrgs = useScopeStore((s) => s.availableOrgs);
  const setDepartment = useScopeStore((s) => s.setDepartment);
  const setProject = useScopeStore((s) => s.setProject);
  const setAvailableProjects = useScopeStore((s) => s.setAvailableProjects);
  const capabilities = useScopeStore((s) => s.capabilities);
  const role = useScopeStore((s) => s.role);
  const hydrated = useScopeStore((s) => s.hydrated);

  // Switching org re-scopes everything — departments, projects, capabilities and
  // every cached query belong to the org being left. A full reload rehydrates
  // the whole shell from the new scope rather than patching it field by field.
  const onSelectOrg = React.useCallback(async (next: OrgRef) => {
    try {
      await sessionService.setActiveOrg(next.id);
      useScopeStore.getState().reset();
      // reset() clears the persisted org, and getScopeHeaders then falls back to
      // the JWT — which still names the org we just left. Seed the new one so
      // requests made before the next bootstrap are already correctly scoped.
      setStoredOrgId(next.id);
      window.location.assign('/');
    } catch {
      toast.error(`Could not switch to ${next.name}`);
    }
  }, []);

  // Simple-only roles (built-in `member` or any role limited to the workspace)
  // are locked into Simple mode — the advanced shell is never rendered for them.
  const simpleOnly = useSimpleOnly();
  // Onboarding resolves from a persisted fast-path marker, so Shell can mount
  // BEFORE the session (capabilities) hydrates, and only a hydrated session can
  // say whether this role is simple-only.
  //
  // Holding on the SIMPLE surface while that resolves reads, to an advanced user
  // refreshing the page, as being thrown into Simple mode — the slower the
  // bootstrap, the longer they sit there. The stored `wx-mode` is the user's own
  // last choice and is authoritative for their own surface, so it is what we
  // render once we may render anything at all; the only thing it cannot settle
  // is RBAC, and RBAC is exactly what `hydrated` is waiting on.
  //
  // So: render neither surface until the session resolves (`holding` below), and
  // no one is shown a surface that is not theirs. An explicit click still wins
  // immediately (`advancedRequested`), so a stuck bootstrap can never strand the
  // user, and the effect below snaps a resolved simple-only role back — RBAC
  // still wins, it just no longer depends on a call that may never complete.
  //
  // Holding is NOT locking. `locked` hides every exit back to advanced, so
  // deriving it from `!hydrated` trapped users on the Simple surface with no
  // mode switch at all whenever /session/bootstrap never landed (the reported
  // bug: "no toggle to leave Simple mode after login"). Only a resolved,
  // capability-backed `simpleOnly` is authoritative enough to hide the exits.
  const [advancedRequested, setAdvancedRequested] = React.useState(false);
  const holding = !hydrated && !advancedRequested;
  const forceSimple = simpleOnly;
  const chooseMode = React.useCallback(
    (m: Mode) => {
      setAdvancedRequested(m === 'advanced');
      setMode(m);
    },
    [setMode],
  );

  // Harness is the source of truth for the project list. The department is sent
  // explicitly (?departmentId=) rather than left to the x-department-id header,
  // so the fetch and its query key always describe the same department — a
  // header read at request time can lag a just-committed switch and cache the
  // previous department's projects under the new key.
  const deptId = department?.id;
  const harnessProjects = useQuery({
    queryKey: ['shell', 'projects', deptId],
    queryFn: () => projectsService.listProjects(100, 0, deptId),
    enabled: !!org?.id,
    staleTime: 0, // always re-fetch when dept key changes so the list is accurate
    throwOnError: false,
  });

  React.useEffect(() => {
    if (harnessProjects.data?.projects) {
      setAvailableProjects(
        harnessProjects.data.projects.map((p) => ({ id: p.id, name: p.name }))
      );
    }
  }, [harnessProjects.data, setAvailableProjects]);

  const { connection } = useConnectionInfo();
  const onGenerateApiKey = useGenerateApiKey();
  const simpleWorkspaces = React.useMemo(
    () => workspacesFromProjects(availableProjects),
    [availableProjects],
  );

  // Reflect theme onto <html> on mount + whenever it changes.
  React.useEffect(() => {
    persistTheme(theme);
  }, [theme]);

  // Global keyboard: ⌘K / Ctrl-K toggles the palette, Esc closes it.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        togglePalette();
      }
      if (e.key === 'Escape') setPaletteOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePalette, setPaletteOpen]);

  const caps = capabilities.size > 0 ? capabilities : null;
  // Nav items marked `orgAdminOnly` are for organization administrators only
  // (none today): the same check the app marketplace's "Talk to team" button uses.
  const orgAdmin = isOrgAdminRole(role);
  const nav = React.useMemo(() => filterNav(undefined, caps, { orgAdmin }), [caps, orgAdmin]);

  // Keep the persisted mode in sync for locked users so a stale `advanced` value
  // never leaks back on the next load or a round-trip route restore.
  // Also clears any pre-hydration "take me to advanced" request, so a resolved
  // simple-only role always wins over the optimistic exit.
  React.useEffect(() => {
    if (!simpleOnly) return;
    setAdvancedRequested(false);
    if (mode !== 'simple') setMode('simple');
  }, [simpleOnly, mode, setMode]);

  // Onboarding is enforced upstream by ProtectedRoute: a user without a project
  // is redirected to the standalone /onboarding route and the Shell is never
  // mounted for them. So by the time we render here the user has completed
  // onboarding and owns at least one project — no in-shell gate is needed.
  //
  // Until the session resolves we cannot know whether this role may see the
  // advanced shell, and guessing either way shows someone a surface that is not
  // theirs. Hold on chrome that belongs to neither mode instead.
  if (holding) {
    return (
      <div
        className="flex h-screen w-full items-center justify-center bg-cs-surface-page"
        role="status"
        aria-live="polite"
        aria-busy="true"
        data-testid="shell-resolving"
      >
        <span className="sr-only">Loading your workspace…</span>
        <span
          aria-hidden="true"
          className="h-6 w-6 animate-spin rounded-full border-2 border-cs-border border-t-transparent"
        />
      </div>
    );
  }

  // Simple-only roles are forced here regardless of the stored mode, so the
  // advanced shell (and any child page like /settings/*) is NEVER mounted for
  // them — and `locked` hides every exit back to advanced.
  if (mode === 'simple' || forceSimple) {
    return (
      <SimpleMode
        theme={theme}
        toggleTheme={toggleTheme}
        // A forced simple-only role may still carry a stored `advanced`; report
        // the surface actually rendered so the switch reads "on".
        mode="simple"
        setMode={chooseMode}
        locked={simpleOnly}
        userInitials={user?.initials ?? '—'}
        connection={connection}
        workspaces={simpleWorkspaces.length ? simpleWorkspaces : undefined}
        onGenerateApiKey={onGenerateApiKey}
      />
    );
  }

  return (
    <div className="flex h-screen w-full overflow-hidden bg-cs-surface-page">
      <Sidebar
        route={route}
        go={go}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        nav={nav}
        org={org}
        credits={credits}
        theme={theme}
        toggleTheme={toggleTheme}
        mode={mode}
        setMode={setMode}
        department={department}
        project={project}
        availableDepartments={availableDepartments}
        availableProjects={availableProjects}
        availableOrgs={availableOrgs}
        onSelectDepartment={setDepartment}
        onSelectProject={setProject}
        onSelectOrg={onSelectOrg}
        canCreateDepartment={rbac.canCreateDepartment(role)}
        canCreateProject={rbac.canCreateProject(role)}
        userInitials={user?.initials ?? '—'}
        userName={user?.name}
        userEmail={user?.email}
        userRole={user?.role}
        showCredits={canViewBilling(caps)}
        onOpenSearch={() => setPaletteOpen(true)}
      />
      <main id="main-content" data-shell-scroll className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
        {children}
      </main>
      <ShellCommandPalette
        open={paletteOpen}
        close={() => setPaletteOpen(false)}
        go={go}
        capabilities={caps}
        orgAdmin={orgAdmin}
      />
    </div>
  );
}
